import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import useLiveQuotes from "../hooks/useLiveQuotes";
import { track } from "../hooks/useAnalytics";
import { usePlan } from "../context/PlanContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FilterBuilder from "../components/FilterBuilder";
import ResultsTable from "../components/ResultsTable";
import SetupCards from "../components/SetupCards";
import Heatmap from "../components/Heatmap";
import Watchlist from "../components/Watchlist";
import PriceChart from "../components/PriceChart";
import TopSetup from "../components/TopSetup";
import SavedScans from "../components/SavedScans";
import Onboarding from "../components/Onboarding";
import RadarSweep from "../components/RadarSweep";
import MiniChartPopover from "../components/MiniChartPopover";
import CountUp from "../components/CountUp";
import { toast } from "sonner";
import { AlertTriangle, RotateCw, Sparkles } from "lucide-react";
import LiveBoard from "../components/LiveBoard";
import DataBadge from "../components/DataBadge";
import { ResultsTableSkeleton, ChartSkeleton } from "../components/Skeleton";
import PromoBanner from "../components/PromoBanner";
import AIAssistant from "../components/AIAssistant";
import ReferralWidget from "../components/ReferralWidget";
import TickerCommunityWidget from "../components/TickerCommunityWidget";
import ShareScanButton from "../components/ShareScanButton";

export default function Dashboard() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    market: "stocks",
    asset_types: ["large_cap"],
    timeframe: "15m",
    indicators: ["low_rsi", "macd_bull_cross"],
    signal_strength: "light",
    direction: null,
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [wlRefresh, setWlRefresh] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [hover, setHover] = useState({ ticker: null, rect: null });
  const [saveTrigger, setSaveTrigger] = useState(0);
  const savedScansRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Fork-consumer: /dashboard?fork=<post_id> → fetch stored scan_setup, apply, clear param.
  useEffect(() => {
    const forkId = searchParams.get("fork");
    if (!forkId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/community/posts/${forkId}/scan-setup`);
        if (cancelled) return;
        const s = data?.scan_setup?.filters || {};
        setFilters((cur) => ({
          ...cur,
          market: (s.assetClasses && s.assetClasses[0]) || cur.market,
          asset_types: s.assetTypes && s.assetTypes.length ? s.assetTypes : cur.asset_types,
          timeframe: s.timeframe || cur.timeframe,
          indicators: s.indicators && s.indicators.length ? s.indicators : cur.indicators,
          signal_strength: s.strength || cur.signal_strength,
          direction: s.direction && s.direction !== "any" ? s.direction : null,
        }));
        toast.success(`Applied @${data.author_username}'s scan setup`);
      } catch (err) {
        const d = err.response?.data?.detail;
        toast.error(d?.message || d || "Could not load shared setup");
      } finally {
        // Always clear the fork param so a refresh doesn't reapply
        const next = new URLSearchParams(searchParams);
        next.delete("fork");
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const liveQuotes = useLiveQuotes();
  const { showUpgrade } = usePlan();

  const runScan = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/api/scanner/scan", filters);
      setResults(data.results);
      if (data.results.length > 0 && !selectedTicker) setSelectedTicker(data.results[0].ticker);
      toast.success(`Scan complete · ${data.count} matches`);
      track("scan_run", {
        market: filters.market,
        timeframe: filters.timeframe,
        indicators_count: (filters.indicators || []).length,
        results_count: data.count,
      });
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      // 402 → gated feature or scan limit. Trigger the universal upgrade modal
      // with a context-appropriate reason instead of a bare toast.
      if (status === 402) {
        const isDaily = /daily scan limit/i.test(detail);
        showUpgrade(isDaily ? "scans_per_day" : "default");
      }
      setError(detail); toast.error(detail);
    } finally { setLoading(false); }
  }, [filters, selectedTicker, showUpgrade]);

  // run once on mount
  useEffect(() => { runScan(); /* eslint-disable-next-line */ }, []);

  // Re-run scan every 8s to keep indicators + matched_filters fresh.
  // Live prices come in real-time through the SSE hook (overlaid below).
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) runScan(); }, 8000);
    return () => clearInterval(id);
  }, [runScan]);

  const addWatchlist = async (r) => {
    try {
      await api.post("/api/watchlist", { ticker: r.ticker, name: r.name, asset_class: r.asset_class });
      toast.success(`${r.ticker} added to watchlist`);
      setWlRefresh((x) => x + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    }
  };

  // Overlay live SSE quotes onto the scan results so prices/change% update
  // in real time without waiting for the next /scan poll.
  const liveResults = useMemo(() => {
    if (!results.length || !liveQuotes.quotes) return results;
    return results.map((r) => {
      const q = liveQuotes.quotes[r.ticker];
      if (!q) return r;
      return {
        ...r,
        price: q.price || r.price,
        change_pct: q.change_pct != null ? q.change_pct : r.change_pct,
        status: q.status || r.status,
        source: q.source || r.source,
        age_sec: q.age_sec != null ? q.age_sec : r.age_sec,
      };
    });
  }, [results, liveQuotes.quotes]);

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return liveResults;
    const q = searchQuery.trim().toUpperCase();
    return liveResults.filter((r) => r.ticker.includes(q) || r.name.toUpperCase().includes(q));
  }, [liveResults, searchQuery]);

  const handleSearch = (q) => {
    const upper = q.trim().toUpperCase();
    if (!upper) return;
    const hit = results.find((r) => r.ticker === upper);
    if (hit) setSelectedTicker(upper);
    track("ticker_search", { ticker: upper, hit: !!hit });
  };

  const kpi = useMemo(() => {
    if (!liveResults.length) return { matches: 0, bullish: 0, bearish: 0, avg: 0 };
    const bullish = liveResults.filter((r) => r.direction === "bullish").length;
    const bearish = liveResults.length - bullish;
    const avg = Math.round(liveResults.reduce((a, r) => a + r.score, 0) / liveResults.length);
    return { matches: liveResults.length, bullish, bearish, avg };
  }, [liveResults]);

  const topSetup = liveResults[0] && liveResults[0].score >= 50 ? liveResults[0] : null;

  // Overall scanner status from the SSE stream
  const scannerStatus = liveQuotes.lastEventAt === 0
    ? "loading"
    : liveQuotes.wsConnected && liveQuotes.liveCount > 0
      ? "live"
      : liveQuotes.connected
        ? "delayed"
        : "stale";

  // Trigger SavedScans "save" modal externally
  const handleSavePreset = () => {
    setSaveTrigger((x) => x + 1);
    // scroll the saved scans panel into view as a UX nicety
    if (savedScansRef.current) savedScansRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="dashboard-page">
      <Header onSearch={handleSearch} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <PromoBanner />

      {/* Onboarding overlay (first visit only) */}
      <Onboarding />

      {/* Floating mini chart on hover */}
      <MiniChartPopover ticker={hover.ticker} anchorRect={hover.rect} timeframe={filters.timeframe} />

      <main id="main-content" className="flex-1 max-w-[1800px] w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Greeting + KPI strip */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
          <div>
            <div className="label-mono">Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ""}</div>
            <div className="flex items-center gap-3 mt-1">
              <h1 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight leading-tight">
                Scanner <span className="aurora-text">Terminal</span>
              </h1>
              <DataBadge
                status={scannerStatus}
                lastEventAt={liveQuotes.lastEventAt}
                liveCount={liveQuotes.liveCount}
                total={liveQuotes.total}
                className="mt-1"
              />
            </div>
            {scannerStatus === "delayed" && (
              <div className="text-[11px] text-white/45 mt-1" data-testid="delayed-banner">
                Showing last-traded prices — US market is closed or out of session. Live ticks resume on next open.
              </div>
            )}
            {scannerStatus === "stale" && (
              <div className="text-[11px] text-[var(--bear)]/85 mt-1" data-testid="stale-banner">
                Live stream temporarily unreachable. Reconnecting…
              </div>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2 lg:gap-3">
            <Kpi label="Matches" value={kpi.matches} live />
            <Kpi label="Bullish" value={kpi.bullish} variant="bull" />
            <Kpi label="Bearish" value={kpi.bearish} variant="bear" />
            <Kpi label="Avg score" value={kpi.avg} />
          </div>
        </div>

        {/* Top setup featured banner */}
        <div className="mb-5">
          <TopSetup setup={topSetup} onOpen={setSelectedTicker} onAddWatchlist={addWatchlist} />
        </div>

        {error && (
          <div className="card-surface p-4 mb-4 flex items-center gap-3 border-[var(--bear)]/30" data-testid="scan-error">
            <AlertTriangle className="w-4 h-4 text-[var(--bear)]" />
            <div className="text-sm text-white/80">{error}</div>
          </div>
        )}

        {/* 3-column dashboard grid */}
        <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr_300px] gap-4 lg:gap-5">
          {/* Left – Filter builder + Saved scans */}
          <aside className="xl:sticky xl:top-[88px] xl:self-start space-y-4">
            <div className="relative">
              <FilterBuilder
                filters={filters}
                setFilters={setFilters}
                onRun={runScan}
                loading={loading}
                userPlan={user?.plan || "free"}
                onSaveScan={handleSavePreset}
              />
              <RadarSweep active={loading} />
            </div>
            <div ref={savedScansRef}>
              <SavedScans
                currentFilters={filters}
                onLoad={(f) => { setFilters(f); setTimeout(() => runScan(), 50); }}
                openSignal={saveTrigger}
              />
            </div>
          </aside>

          {/* Center – Chart + results + heatmap */}
          <section className="space-y-4 lg:space-y-5 min-w-0">
            {loading && !selectedTicker ? <ChartSkeleton /> : <PriceChart ticker={selectedTicker} timeframe={filters.timeframe} />}
            <SetupCards results={filteredResults} onHover={(t, r) => setHover({ ticker: t, rect: r })} />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-white/60" />
                <span className="label-mono">All results</span>
              </div>
              <div className="flex items-center gap-2">
                <ShareScanButton filters={filters} results={filteredResults} />
                <button onClick={runScan} className="btn-ghost flex items-center gap-2 text-xs py-2 px-3" data-testid="refresh-scan-btn">
                  <RotateCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>
            <div className="relative">
              {loading && liveResults.length === 0 ? (
                <ResultsTableSkeleton />
              ) : (
                <ResultsTable
                  results={filteredResults}
                  onSelect={setSelectedTicker}
                  selectedTicker={selectedTicker}
                  onAddWatchlist={addWatchlist}
                  onHover={(t, r) => setHover({ ticker: t, rect: r })}
                />
              )}
              <RadarSweep active={loading} label="Scanning markets" />
            </div>
            <Heatmap results={liveResults} />
            <LiveBoard onSelectTicker={setSelectedTicker} />
          </section>

          {/* Right – Watchlist + plan info + refer-a-friend */}
          <aside className="xl:sticky xl:top-[88px] xl:self-start space-y-4">
            <Watchlist refreshKey={wlRefresh} onSelectTicker={setSelectedTicker} />
            <TickerCommunityWidget ticker={selectedTicker} />
            <PlanCard plan={user?.plan || "free"} />
            <ReferralWidget />
          </aside>
        </div>

        <div className="card-surface p-4 mt-6 flex items-start gap-3" data-testid="dashboard-disclaimer">
          <AlertTriangle className="w-4 h-4 text-white/60 mt-0.5 shrink-0" />
          <div className="text-xs text-white/60 leading-relaxed">
            Educational purposes only. Not financial advice. Market data may be delayed depending on provider.
            Trading involves substantial risk of loss.
          </div>
        </div>
      </main>
      <Footer />
      <AIAssistant tickerContext={selectedTicker} />
    </div>
  );
}

function Kpi({ label, value, variant, live }) {
  const cls = variant === "bull" ? "text-bull" : variant === "bear" ? "text-bear" : "text-white";
  return (
    <div className="card-surface px-3 sm:px-4 py-2.5 min-w-[88px] relative overflow-hidden" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {live && (
        <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[var(--bull)] pulse-dot" />
      )}
      <div className="label-mono">{label}</div>
      <div className={`font-mono text-xl sm:text-2xl font-semibold tabular-nums mt-1 ${cls}`}>
        <CountUp value={value} />
      </div>
    </div>
  );
}

function PlanCard({ plan }) {
  const p = plan === "pro" ? "premium" : plan === "essential" ? "premium" : plan === "elite" ? "executive" : plan;
  const isExec = p === "executive";
  const isPrem = p === "premium";
  return (
    <div className="card-surface p-5 relative overflow-hidden" data-testid="plan-card-side">
      <div className="absolute inset-0 pointer-events-none opacity-50"
        style={{ background: "radial-gradient(circle at 100% 0%, rgba(108,92,231,0.25), transparent 50%)" }} />
      <div className="relative">
        <div className="label-mono">Subscription</div>
        <div className="text-2xl text-white font-semibold tracking-tight mt-1 capitalize">{p} plan</div>
        <div className="mt-3 space-y-1 text-xs text-white/55">
          {p === "free" && (<>
            <div>· 1 watchlist (5 symbols)</div>
            <div>· 15 scans / day</div>
            <div>· Delayed market data</div>
          </>)}
          {isPrem && (<>
            <div>· Unlimited watchlists</div>
            <div>· Live scanner + SMS alerts</div>
            <div>· AI Trading Assistant</div>
          </>)}
          {isExec && (<>
            <div>· Everything in Premium</div>
            <div>· Professional Backtester</div>
            <div>· CSV/Excel/PDF exports</div>
          </>)}
        </div>
        {!isExec && (
          <a href="/pricing" className="btn-accent mt-4 inline-flex" data-testid="plan-card-upgrade">Upgrade</a>
        )}
      </div>
    </div>
  );
}
